/**
 * Measures the width of a text string in pixels when rendered in the document's body.
 * text - The text to measure.
 */
export function measureSpanWidth(text) {
  // Create a new 'span' element
  const span = document.createElement("span");
  // Set styles to ensure consistent measurement
  span.style.visibility = "hidden";
  span.style.position = "absolute";
  span.style.whiteSpace = "nowrap";
  span.style.fontSize = "20px";
  span.style.fontWeight = "700";
  span.style.fontFamily = `-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
    "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
    sans-serif`;
  // Set the text content of the span to the input text
  span.textContent = text;
  // Append the span to the body to measure its width
  document.body.appendChild(span);
  // Get the width of the span from its bounding rectangle
  const width = span.getBoundingClientRect().width;
  // Remove the span from the body after measurement
  document.body.removeChild(span);
  // Return the measured width
  return width;
}

/**
 * Calculates a list of items that can be rendered within a given width.
 * 
 * This function iteratively tries to fit items from the `localItems` array into the available `outerWidth`.
 * If the total width of `localItems` and `lastItem` is less than `outerWidth` by at least 10 pixels, the full list is returned.
 * Otherwise, it unfolds the array one step at a time and checks if the unfolded list plus the `lastItem` can fit.
 * If the unfolded list cannot fit, the function returns the last successfully calculated list.
 * 
 * outerWidth - The width available for rendering the list.
 * localItems - An array of objects where each object represents an item with a `width` property.
 * lastItem - An object representing the last item to be rendered, with a `width` property.
 */
export function calculateRenderedList(outerWidth, localItems, lastItem) {
  // Calculate the total width of all items plus the last item
  const totalWidth =
    localItems.reduce((acc, curr) => acc + curr.width, 0) + lastItem.width;

  // If there's enough space, return the full list
  if (outerWidth - totalWidth > 10) return localItems;

  // Initialize the current list with the full list of local items
  let current = [...[localItems]];
  let next;
  let lastItemWidth = lastItem.width;

  // Iterate through the local items
  for (let index = 0; index <= localItems.length - 1; index++) {
    // Unfold the current list by one step
    next = unfoldArrayOneStep([...current]);
    // Calculate the width of the unfolded list
    const nextWidth = getWidth(next);
    // Check if the unfolded list plus the last item fits within the outer width
    if (outerWidth - (nextWidth + lastItemWidth) < 14) {
      // If not, return the last successfully calculated list
      return current;
    } else {
      // If it fits, update the current list to the unfolded list
      current = [...next];
    }
  }
}


/**
 * Unfolds an array by one step, redistributing the central array elements to the sides.
 * 
 * The function takes an array that may contain nested arrays. It iterates through the array,
 * separating the elements into left, center, and right groups based on their position relative
 * to the nested array (considered the center). It then moves the last element of the center
 * to the right side if the left side is longer, or the first element of the center to the left
 * side if the right side is longer, effectively unfolding the array by one step.
 * 
 * array - The array to unfold, which may contain nested arrays.
 */
export function unfoldArrayOneStep(array) {
  let leftSide = [];
  let rightSide = [];
  let center = [];
  let sideChange = false;

  // Distribute items to left, center, or right
  array.forEach((item) => {
    if (Array.isArray(item)) {
      sideChange = true;
      center = [...item];
    } else {
      if (!sideChange) {
        leftSide = [...leftSide, item];
      } else {
        rightSide = [...rightSide, item];
      }
    }
  });

  // Decide where to push the element from the center
  if (leftSide.length >= rightSide.length) {
    // Push the last element of center to the right side
    rightSide = [center.pop(), ...rightSide];
  } else {
    // Push the first element of center to the left side
    leftSide = [...leftSide, center.shift()];
  }

  // Combine the left, center, and right into the result
  const result = [...leftSide, ...center.length ? [center] : [], ...rightSide];

  return result;
}


/**
 * Calculate the total width from an array of objects.
 * 
 * arr - The array containing objects with a width property.
 */
export function getWidth(arr) {
  let acc = 0;
  arr.forEach((item) => {
    // Check if the current item is not an array
    if (!Array.isArray(item)) {
      // Add the item's width to the accumulator
      acc += item.width;
    }
  });
  return acc;
}